/**
 * Template Management View Component
 * 
 * Split-pane view for managing state templates (property templates)
 * with template list on left and details/editing on right
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Input,
  message,
  Tabs,
  Form,
  Select,
  Checkbox,
  Modal,
  InputNumber,
  Tooltip
} from 'antd';
import type { SortOrder } from 'antd/es/table/interface';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import { ManagementForm } from '../common/ManagementForm';
import { GlobalPropertySelector } from '../StateTemplates/GlobalPropertySelector';
import { PropertyChangesEditor } from '../PrimeModel/PropertyChangesEditor';
import { ValidationRulesEditor } from '../Parameters/ValidationRulesEditor';
import {
  StandardOperationalState,
  StandardImprovementState,
  StandardTransition
} from '../../types/standardStates';
import type { TransitionParameters, ValidationRules, PropertyChange } from '../../types/transitionParameters';
import styles from './GlobalPropertyTableView.module.css';

const { Title, Text } = Typography;
const { Search, TextArea } = Input;
const { Option } = Select;

// Helper functions to deduplicate states and transitions by name
const deduplicateStates = (statesArray: any[]): any[] => {
  const seen = new Set<string>();
  return statesArray.filter(state => {
    if (seen.has(state.name)) {
      return false;
    }
    seen.add(state.name);
    return true;
  });
};

const deduplicateTransitions = (transitionsArray: any[]): any[] => {
  const seen = new Set<string>();
  return transitionsArray.filter(transition => {
    if (seen.has(transition.name)) {
      return false;
    }
    seen.add(transition.name);
    return true;
  });
};

interface StateTemplateState {
  id?: string;
  template_id?: string;
  name: string;
  description?: string;
  is_initial: boolean;
  is_final: boolean;
  is_improvement: boolean;
  is_resource_active: boolean;
  state_type: string;
  sequence_order: number;
  default_parameters?: Record<string, any>;
}

interface StateTemplateTransition {
  id?: string;
  template_id?: string;
  name: string;
  description?: string;
  from_state: string | null;
  to_state: string;
  default_transition_type: string;
  is_secondary: boolean;
  is_improvement: boolean;
  sequence_order: number;
  default_parameters?: Record<string, any>;
  validation_rules?: Record<string, any>;
}

interface StateTemplatePropertyMapping {
  id?: string;
  template_id?: string;
  property_key: string;
  property_type: string;
  global_option_catalog_ref?: string;
  created_in_states?: string[];
  updated_in_states?: string[];
  referenced_artifact_type_ids?: string[];
  is_improvement?: boolean;
}

interface StateTemplate {
  id: string;
  name: string;
  description?: string;
  version: string;
  applies_to_artifact_types?: string[];
  tags?: string[];
  created_at: string;
  updated_at: string;
  states: StateTemplateState[];
  transitions: StateTemplateTransition[];
  property_mappings: StateTemplatePropertyMapping[];
}

interface TemplateManagementViewProps {
  onNavigateToProperty?: (propertyKey: string) => void;
  onNavigateToPropertiesTab?: () => void;
}

export const TemplateManagementView: React.FC<TemplateManagementViewProps> = ({
  onNavigateToProperty,
  onNavigateToPropertiesTab
}) => {
  const [templates, setTemplates] = useState<StateTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<StateTemplate | null>(null);
  const [searchText, setSearchText] = useState('');
  const [activeDetailsTab, setActiveDetailsTab] = useState('details');
  
  // Template editing state
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [templateForm] = Form.useForm();
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [copyingFromTemplate, setCopyingFromTemplate] = useState<StateTemplate | null>(null);
  
  // States and transitions editing state
  const [states, setStates] = useState<StateTemplateState[]>([]);
  const [transitions, setTransitions] = useState<StateTemplateTransition[]>([]);
  const [propertyMappings, setPropertyMappings] = useState<StateTemplatePropertyMapping[]>([]);
  const [showStateModal, setShowStateModal] = useState(false);
  const [editingState, setEditingState] = useState<StateTemplateState | null>(null);
  const [stateForm] = Form.useForm();
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [editingTransition, setEditingTransition] = useState<StateTemplateTransition | null>(null);
  const [transitionForm] = Form.useForm();
  const [transitionPropertyChanges, setTransitionPropertyChanges] = useState<PropertyChange[]>([]);
  const [transitionValidationRules, setTransitionValidationRules] = useState<ValidationRules>({});
  const [transitionModalActiveTab, setTransitionModalActiveTab] = useState<string>('basic');
  const [stateFilter, setStateFilter] = useState<'all' | 'improvement' | 'operational'>('all');
  const [transitionFilter, setTransitionFilter] = useState<'all' | 'improvement' | 'operational'>('all');
  const [showPropertySelector, setShowPropertySelector] = useState(false);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/state-templates');
      const templatesData = response.data || [];
      
      const templatesNeedingFullData = templatesData.filter((template: StateTemplate) => {
        const statesCount = Array.isArray(template.states) ? template.states.length : 0;
        const transitionsCount = Array.isArray(template.transitions) ? template.transitions.length : 0;
        const propertiesCount = Array.isArray(template.property_mappings) ? template.property_mappings.length : 0;
        return statesCount === 0 && transitionsCount === 0 && propertiesCount === 0;
      });
      
      let enrichedTemplates = [...templatesData];
      if (templatesNeedingFullData.length > 0) {
        const fullDataPromises = templatesNeedingFullData.map(async (template: StateTemplate) => {
          try {
            const fullResponse = await apiClient.get(`/state-templates/${template.id}`);
            return fullResponse.data;
          } catch (error) {
            console.error(`Error fetching full data for template ${template.id}:`, error);
            return template;
          }
        });
        
        const fullDataTemplates = await Promise.all(fullDataPromises);
        enrichedTemplates = templatesData.map((template: StateTemplate) => {
          const fullData = fullDataTemplates.find((ft: StateTemplate) => ft.id === template.id);
          return fullData || template;
        });
      }
      
      const cleanedTemplates = enrichedTemplates.map((template: StateTemplate) => {
        const states = Array.isArray(template.states) ? template.states : [];
        const transitions = Array.isArray(template.transitions) ? template.transitions : [];
        const propertyMappings = Array.isArray(template.property_mappings) ? template.property_mappings : [];
        
        return {
          ...template,
          states: deduplicateStates(states),
          transitions: deduplicateTransitions(transitions.map((t: StateTemplateTransition) => ({
            ...t,
            is_improvement: t.is_improvement ?? false
          }))),
          property_mappings: propertyMappings.map((p: StateTemplatePropertyMapping) => ({
            ...p,
            is_improvement: p.is_improvement ?? false
          }))
        };
      });
      
      setTemplates(cleanedTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      void message.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  // Load full template data when selected
  useEffect(() => {
    const loadFullTemplateData = async () => {
      if (selectedTemplate) {
        try {
          const response = await apiClient.get(`/state-templates/${selectedTemplate.id}`);
          const fullTemplate = response.data;
          const cleanedStates = deduplicateStates(fullTemplate.states || []);
          const cleanedTransitions = deduplicateTransitions((fullTemplate.transitions || []).map((t: StateTemplateTransition) => ({
            ...t,
            is_improvement: t.is_improvement ?? false
          })));
          const cleanedMappings = (fullTemplate.property_mappings || []).map((p: StateTemplatePropertyMapping) => ({
            ...p,
            is_improvement: p.is_improvement ?? false
          }));
          
          setStates(cleanedStates);
          setTransitions(cleanedTransitions);
          setPropertyMappings(cleanedMappings);
          
          // Update selected template with full data
          setSelectedTemplate({
            ...fullTemplate,
            states: cleanedStates,
            transitions: cleanedTransitions,
            property_mappings: cleanedMappings
          });
        } catch (error) {
          console.error('Error loading template data:', error);
        }
      }
    };
    
    void loadFullTemplateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate?.id]);

  const filteredTemplates = useMemo(() => {
    if (!searchText) return templates;
    const lowerSearch = searchText.toLowerCase();
    return templates.filter(template =>
      template.name.toLowerCase().includes(lowerSearch) ||
      template.description?.toLowerCase().includes(lowerSearch) ||
      template.version.toLowerCase().includes(lowerSearch)
    );
  }, [templates, searchText]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text: string, record: StateTemplate) => (
        <Space>
          <strong>{text}</strong>
          {record.tags && Array.isArray(record.tags) && record.tags.length > 0 && (
            <Space size={4}>
              {record.tags.map((tag, idx) => (
                <Tag key={idx} color="blue">{tag}</Tag>
              ))}
            </Space>
          )}
        </Space>
      )
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: 100
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'States',
      key: 'states_count',
      width: 80,
      render: (_: any, record: StateTemplate) => (
        <Tag color="cyan">{record.states?.length || 0}</Tag>
      )
    },
    {
      title: 'Transitions',
      key: 'transitions_count',
      width: 100,
      render: (_: any, record: StateTemplate) => (
        <Tag color="green">{record.transitions?.length || 0}</Tag>
      )
    },
    {
      title: 'Properties',
      key: 'properties_count',
      width: 100,
      render: (_: any, record: StateTemplate) => (
        <Tag color="purple">{record.property_mappings?.length || 0}</Tag>
      )
    }
  ];

  const handleRowClick = (record: StateTemplate) => {
    setSelectedTemplate(record);
    setActiveDetailsTab('details');
    setIsCreatingTemplate(false);
    templateForm.setFieldsValue({
      name: record.name,
      description: record.description,
      version: record.version,
      applies_to_artifact_types: record.applies_to_artifact_types,
      tags: record.tags
    });
  };

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setIsCreatingTemplate(true);
    setCopyingFromTemplate(null);
    templateForm.resetFields();
    templateForm.setFieldsValue({ version: '1.0.0' });
    setActiveDetailsTab('details');
  };

  const handleCopyTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      const response = await apiClient.get(`/state-templates/${selectedTemplate.id}`);
      const fetchedTemplate = response.data;
      
      const cleanedTemplate = {
        ...fetchedTemplate,
        states: deduplicateStates(fetchedTemplate.states || []),
        transitions: deduplicateTransitions((fetchedTemplate.transitions || []).map((t: StateTemplateTransition) => ({
          ...t,
          is_improvement: t.is_improvement ?? false
        }))),
        property_mappings: (fetchedTemplate.property_mappings || []).map((p: StateTemplatePropertyMapping) => ({
          ...p,
          is_improvement: p.is_improvement ?? false
        }))
      };
      
      setCopyingFromTemplate(cleanedTemplate);
      setIsCreatingTemplate(true);
      templateForm.setFieldsValue({
        name: `Copy of ${cleanedTemplate.name}`,
        description: cleanedTemplate.description,
        version: '1.0.0',
        applies_to_artifact_types: cleanedTemplate.applies_to_artifact_types,
        tags: cleanedTemplate.tags
      });
      setSelectedTemplate(null);
    } catch (error: any) {
      void message.error('Failed to load template for copying: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSaveTemplate = async (values: any) => {
    setIsSavingTemplate(true);
    try {
      const cleanedValues = {
        ...values,
        tags: values.tags && values.tags.length > 0 ? values.tags : null,
        applies_to_artifact_types: values.applies_to_artifact_types && values.applies_to_artifact_types.length > 0 ? values.applies_to_artifact_types : null
      };

      if (selectedTemplate && !isCreatingTemplate) {
        await apiClient.put(`/state-templates/${selectedTemplate.id}`, cleanedValues);
        void message.success('Template updated successfully');
      } else {
        let templateData: any = {
          ...cleanedValues,
          states: [],
          transitions: [],
          property_mappings: []
        };

        if (copyingFromTemplate) {
          templateData.states = (copyingFromTemplate.states || []).map(state => ({
            name: state.name,
            description: state.description,
            is_initial: state.is_initial,
            is_final: state.is_final,
            is_improvement: state.is_improvement,
            is_resource_active: state.is_resource_active,
            state_type: state.state_type,
            sequence_order: state.sequence_order,
            default_parameters: state.default_parameters
          }));

          templateData.transitions = (copyingFromTemplate.transitions || []).map(transition => ({
            name: transition.name,
            description: transition.description,
            from_state: transition.from_state,
            to_state: transition.to_state,
            default_transition_type: transition.default_transition_type,
            is_secondary: transition.is_secondary,
            is_improvement: transition.is_improvement || false,
            sequence_order: transition.sequence_order,
            default_parameters: transition.default_parameters,
            validation_rules: transition.validation_rules
          }));

          templateData.property_mappings = (copyingFromTemplate.property_mappings || []).map(mapping => ({
            property_key: mapping.property_key,
            property_type: mapping.property_type,
            global_option_catalog_ref: mapping.global_option_catalog_ref,
            created_in_states: mapping.created_in_states,
            updated_in_states: mapping.updated_in_states,
            referenced_artifact_type_ids: mapping.referenced_artifact_type_ids,
            is_improvement: mapping.is_improvement || false
          }));
        }

        const response = await apiClient.post('/state-templates', templateData);
        const createdTemplate = response.data;
        
        if (createdTemplate && createdTemplate.id) {
          void message.success(`Template created successfully with ${createdTemplate.states?.length || 0} states, ${createdTemplate.transitions?.length || 0} transitions, and ${createdTemplate.property_mappings?.length || 0} properties`);
          setSelectedTemplate(createdTemplate);
          setIsCreatingTemplate(false);
        }
      }

      await fetchTemplates();
      setCopyingFromTemplate(null);
    } catch (error: any) {
      console.error('Error saving template:', error);
      void message.error('Failed to save template: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      await apiClient.delete(`/state-templates/${selectedTemplate.id}`);
      void message.success('Template deleted successfully');
      setSelectedTemplate(null);
      await fetchTemplates();
    } catch (error: any) {
      void message.error('Failed to delete template: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCancelTemplate = () => {
    setIsCreatingTemplate(false);
    setCopyingFromTemplate(null);
    if (selectedTemplate) {
      templateForm.setFieldsValue({
        name: selectedTemplate.name,
        description: selectedTemplate.description,
        version: selectedTemplate.version,
        applies_to_artifact_types: selectedTemplate.applies_to_artifact_types,
        tags: selectedTemplate.tags
      });
    } else {
      templateForm.resetFields();
    }
  };

  // Get max sequence order
  const getMaxSequence = (items: Array<{ sequence_order: number }>) => {
    if (items.length === 0) return 0;
    return Math.max(...items.map(item => item.sequence_order || 0), 0);
  };

  // Get state by name
  const getStateByName = (name: string | null): StateTemplateState | null => {
    if (!name) return null;
    return states.find(s => s.name === name) || null;
  };

  // Auto-set transition improvement from states
  const autoSetTransitionImprovement = (fromStateName: string | null, toStateName: string): boolean => {
    const toState = getStateByName(toStateName);
    if (toState) {
      return toState.is_improvement;
    }
    return false;
  };

  // State management handlers
  const handleAddState = () => {
    setEditingState(null);
    const maxSeq = getMaxSequence(states);
    stateForm.resetFields();
    stateForm.setFieldsValue({
      sequence_order: maxSeq + 1,
      state_type: 'OPERATIONAL',
      is_initial: false,
      is_final: false,
      is_improvement: false,
      is_resource_active: false
    });
    setShowStateModal(true);
  };

  const handleEditState = (state: StateTemplateState) => {
    setEditingState(state);
    stateForm.setFieldsValue(state);
    setShowStateModal(true);
  };

  const handleDeleteState = (stateName: string) => {
    setStates(prevStates => prevStates.filter(s => s.name !== stateName));
    setTransitions(prevTransitions => prevTransitions.filter(t => t.from_state !== stateName && t.to_state !== stateName));
    void handleSaveStatesAndTransitions();
  };

  const handleMoveStateSequence = (stateName: string, direction: 'up' | 'down') => {
    setStates(prevStates => {
      const sorted = [...prevStates].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
      const index = sorted.findIndex(s => s.name === stateName);
      if (index === -1) return prevStates;
      
      if (direction === 'up' && index > 0) {
        const temp = sorted[index].sequence_order;
        sorted[index].sequence_order = sorted[index - 1].sequence_order;
        sorted[index - 1].sequence_order = temp;
      } else if (direction === 'down' && index < sorted.length - 1) {
        const temp = sorted[index].sequence_order;
        sorted[index].sequence_order = sorted[index + 1].sequence_order;
        sorted[index + 1].sequence_order = temp;
      }
      
      return sorted;
    });
    void handleSaveStatesAndTransitions();
  };

  const handleSaveState = async () => {
    try {
      const values = await stateForm.validateFields();
      const seqOrder = typeof values.sequence_order === 'string' ? parseInt(values.sequence_order) || 0 : values.sequence_order || 0;
      const stateData: StateTemplateState = {
        ...values,
        sequence_order: seqOrder
      };

      if (editingState) {
        setStates(prevStates => {
          const updated = prevStates.map(s => s.name === editingState.name ? stateData : s);
          return updated;
        });
      } else {
        setStates(prevStates => {
          if (prevStates.some(s => s.name === stateData.name)) {
            void message.error(`A state with the name "${stateData.name}" already exists`);
            return prevStates;
          }
          const updated = [...prevStates, stateData];
          return updated;
        });
      }

      setShowStateModal(false);
      setEditingState(null);
      stateForm.resetFields();
      void handleSaveStatesAndTransitions();
    } catch (error) {
      console.error('Error saving state:', error);
    }
  };

  // Transition management handlers
  const handleAddTransition = () => {
    setEditingTransition(null);
    const maxSeq = getMaxSequence(transitions);
    transitionForm.resetFields();
    transitionForm.setFieldsValue({
      sequence_order: maxSeq + 1,
      default_transition_type: 'PROCESSITEM',
      is_secondary: false,
      is_improvement: false,
      from_state: null,
      to_state: undefined
    });
    setTransitionPropertyChanges([]);
    setTransitionValidationRules({});
    setTransitionModalActiveTab('basic');
    setShowTransitionModal(true);
  };

  const handleEditTransition = (transition: StateTemplateTransition) => {
    setEditingTransition(transition);
    transitionForm.setFieldsValue(transition);
    
    let propertyChanges: PropertyChange[] = [];
    let validationRules: ValidationRules = {};
    
    if (transition.default_parameters) {
      const params = transition.default_parameters as TransitionParameters;
      propertyChanges = params.property_changes || [];
    }
    
    if (transition.validation_rules) {
      validationRules = transition.validation_rules as ValidationRules;
    }
    
    setTransitionPropertyChanges(propertyChanges);
    setTransitionValidationRules(validationRules);
    setTransitionModalActiveTab('basic');
    setShowTransitionModal(true);
  };

  const handleDeleteTransition = (transitionName: string) => {
    setTransitions(prevTransitions => prevTransitions.filter(t => t.name !== transitionName));
    void handleSaveStatesAndTransitions();
  };

  const handleMoveTransitionSequence = (transitionName: string, direction: 'up' | 'down') => {
    setTransitions(prevTransitions => {
      const sorted = [...prevTransitions].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
      const index = sorted.findIndex(t => t.name === transitionName);
      if (index === -1) return prevTransitions;
      
      if (direction === 'up' && index > 0) {
        const temp = sorted[index].sequence_order;
        sorted[index].sequence_order = sorted[index - 1].sequence_order;
        sorted[index - 1].sequence_order = temp;
      } else if (direction === 'down' && index < sorted.length - 1) {
        const temp = sorted[index].sequence_order;
        sorted[index].sequence_order = sorted[index + 1].sequence_order;
        sorted[index + 1].sequence_order = temp;
      }
      
      return sorted;
    });
    void handleSaveStatesAndTransitions();
  };

  const handleSaveTransition = async () => {
    try {
      const values = await transitionForm.validateFields();
      const seqOrder = typeof values.sequence_order === 'string' ? parseInt(values.sequence_order) || 0 : values.sequence_order || 0;
      const isImprovement = autoSetTransitionImprovement(values.from_state, values.to_state);
      
      const defaultParameters: TransitionParameters = {
        property_changes: transitionPropertyChanges.length > 0 ? transitionPropertyChanges : undefined
      };

      const transitionData: StateTemplateTransition = {
        ...values,
        sequence_order: seqOrder,
        is_improvement: isImprovement,
        default_parameters: Object.keys(defaultParameters).length > 0 && defaultParameters.property_changes ? defaultParameters : undefined,
        validation_rules: Object.keys(transitionValidationRules).length > 0 ? transitionValidationRules : undefined
      };

      if (editingTransition) {
        setTransitions(prevTransitions => {
          const updated = prevTransitions.map(t => t.name === editingTransition.name ? transitionData : t);
          return updated;
        });
      } else {
        setTransitions(prevTransitions => {
          if (prevTransitions.some(t => t.name === transitionData.name)) {
            void message.error(`A transition with the name "${transitionData.name}" already exists`);
            return prevTransitions;
          }
          const updated = [...prevTransitions, transitionData];
          return updated;
        });
      }

      setShowTransitionModal(false);
      setEditingTransition(null);
      setTransitionPropertyChanges([]);
      setTransitionValidationRules({});
      transitionForm.resetFields();
      void handleSaveStatesAndTransitions();
    } catch (error) {
      console.error('Error saving transition:', error);
    }
  };

  // Save states and transitions to backend
  const handleSaveStatesAndTransitions = async () => {
    if (!selectedTemplate) return;
    
    try {
      const uniqueStates = deduplicateStates(states);
      const uniqueTransitions = deduplicateTransitions(transitions);

      const cleanedStates = uniqueStates.map(state => ({
        name: state.name,
        description: state.description || undefined,
        is_initial: state.is_initial || false,
        is_final: state.is_final || false,
        is_improvement: state.is_improvement || false,
        state_type: state.state_type || 'OPERATIONAL',
        is_resource_active: state.is_resource_active || false,
        sequence_order: typeof state.sequence_order === 'string' ? parseInt(state.sequence_order) || 0 : state.sequence_order || 0,
        default_parameters: state.default_parameters || undefined
      }));

      const cleanedTransitions = uniqueTransitions.map(transition => ({
        name: transition.name,
        description: transition.description || undefined,
        from_state: transition.from_state || null,
        to_state: transition.to_state,
        default_transition_type: transition.default_transition_type || 'PROCESSITEM',
        is_secondary: transition.is_secondary || false,
        is_improvement: transition.is_improvement || false,
        sequence_order: typeof transition.sequence_order === 'string' ? parseInt(transition.sequence_order) || 0 : transition.sequence_order || 0,
        default_parameters: transition.default_parameters || undefined,
        validation_rules: transition.validation_rules || undefined
      }));

      await apiClient.put(`/state-templates/${selectedTemplate.id}`, {
        states: cleanedStates,
        transitions: cleanedTransitions
      });
      
      await fetchTemplates();
    } catch (error: any) {
      console.error('Error saving states/transitions:', error);
      void message.error('Failed to save: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Property management handlers
  const handleAddProperty = () => {
    if (!selectedTemplate) return;
    setShowPropertySelector(true);
  };

  const handlePropertySelect = async (propertyKeys: string[], _includeDependencies: boolean) => {
    if (!selectedTemplate) return;
    
    try {
      // Add multiple properties
      const addPromises = propertyKeys.map(async (propertyKey) => {
        const mappingData: any = {
          property_key: propertyKey,
          property_type: 'string', // Default, can be updated later
          is_improvement: false
        };
        return apiClient.post(`/state-templates/${selectedTemplate.id}/property-mappings`, mappingData);
      });

      await Promise.all(addPromises);
      
      await fetchTemplates();
      // Reload template data
      const templateResponse = await apiClient.get(`/state-templates/${selectedTemplate.id}`);
      const fullTemplate = templateResponse.data;
      setPropertyMappings(fullTemplate.property_mappings || []);
      setSelectedTemplate({
        ...selectedTemplate,
        property_mappings: fullTemplate.property_mappings || []
      });
      
      setShowPropertySelector(false);
    } catch (error: any) {
      void message.error('Failed to add properties: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteProperty = async (propertyId: string | undefined, propertyKey: string) => {
    if (!selectedTemplate) return;
    
    if (propertyId) {
      try {
        await apiClient.delete(`/template-property-mappings/${propertyId}`);
        void message.success('Property removed from template');
        setPropertyMappings(prev => prev.filter(p => p.id !== propertyId));
        await fetchTemplates();
      } catch (error: any) {
        void message.error('Failed to remove property: ' + (error.response?.data?.detail || error.message));
      }
    } else {
      setPropertyMappings(prev => prev.filter(p => p.property_key !== propertyKey));
    }
  };

  const handleEditProperty = (propertyKey: string) => {
    if (onNavigateToProperty && onNavigateToPropertiesTab) {
      onNavigateToPropertiesTab();
      setTimeout(() => {
        onNavigateToProperty(propertyKey);
      }, 100);
    }
  };

  const handleCreateNewProperty = () => {
    if (onNavigateToPropertiesTab) {
      onNavigateToPropertiesTab();
    }
  };

  // Filtered data
  const filteredStates = states.filter(state => {
    if (stateFilter === 'improvement') return state.is_improvement;
    if (stateFilter === 'operational') return !state.is_improvement;
    return true;
  });

  const filteredTransitions = transitions.filter(transition => {
    if (transitionFilter === 'improvement') return transition.is_improvement;
    if (transitionFilter === 'operational') return !transition.is_improvement;
    return true;
  });

  const sortedStates = [...filteredStates].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
  const sortedTransitions = [...filteredTransitions].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));

  // State columns
  const stateColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: StateTemplateState, b: StateTemplateState) => a.name.localeCompare(b.name),
      sortDirections: ['ascend', 'descend'] as SortOrder[],
      render: (text: string, record: StateTemplateState) => (
        <Space>
          <strong>{text}</strong>
          {record.is_initial && <Tag color="green">Initial</Tag>}
          {record.is_final && <Tag color="red">Final</Tag>}
          {record.is_improvement && <Tag color="purple">Improvement</Tag>}
        </Space>
      )
    },
    {
      title: 'Type',
      dataIndex: 'state_type',
      key: 'state_type',
      sorter: (a: StateTemplateState, b: StateTemplateState) => a.state_type.localeCompare(b.state_type),
      sortDirections: ['ascend', 'descend'] as SortOrder[],
      render: (type: string) => <Tag>{type}</Tag>
    },
    {
      title: 'Sequence',
      dataIndex: 'sequence_order',
      key: 'sequence_order',
      sorter: (a: StateTemplateState, b: StateTemplateState) => (a.sequence_order || 0) - (b.sequence_order || 0),
      sortDirections: ['ascend', 'descend'] as SortOrder[],
      width: 100
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_: any, record: StateTemplateState) => {
        const sorted = [...states].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
        const sortedIndex = sorted.findIndex(s => s.name === record.name);
        const canMoveUp = sortedIndex > 0;
        const canMoveDown = sortedIndex < sorted.length - 1;
        
        return (
          <Space>
            <Tooltip title="Move up">
              <Button 
                size="small" 
                icon={<ArrowUpOutlined />} 
                onClick={() => handleMoveStateSequence(record.name, 'up')}
                disabled={!canMoveUp}
              />
            </Tooltip>
            <Tooltip title="Move down">
              <Button 
                size="small" 
                icon={<ArrowDownOutlined />} 
                onClick={() => handleMoveStateSequence(record.name, 'down')}
                disabled={!canMoveDown}
              />
            </Tooltip>
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEditState(record)} />
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: 'Delete State',
                  icon: <ExclamationCircleOutlined />,
                  content: `Are you sure you want to delete "${record.name}"?`,
                  onOk: () => handleDeleteState(record.name)
                });
              }}
            />
          </Space>
        );
      }
    }
  ];

  // Transition columns
  const transitionColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: StateTemplateTransition, b: StateTemplateTransition) => a.name.localeCompare(b.name),
      sortDirections: ['ascend', 'descend'] as SortOrder[]
    },
    {
      title: 'From',
      dataIndex: 'from_state',
      key: 'from_state',
      render: (text: string | null) => text || <Tag>CREATION</Tag>
    },
    {
      title: 'To',
      dataIndex: 'to_state',
      key: 'to_state'
    },
    {
      title: 'Type',
      dataIndex: 'default_transition_type',
      key: 'default_transition_type',
      render: (type: string, record: StateTemplateTransition) => (
        <Space>
          <Tag>{type}</Tag>
          {record.is_improvement && <Tag color="purple">Improvement</Tag>}
        </Space>
      )
    },
    {
      title: 'Sequence',
      dataIndex: 'sequence_order',
      key: 'sequence_order',
      width: 100
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_: any, record: StateTemplateTransition) => {
        const sorted = [...transitions].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
        const sortedIndex = sorted.findIndex(t => t.name === record.name);
        const canMoveUp = sortedIndex > 0;
        const canMoveDown = sortedIndex < sorted.length - 1;
        
        return (
          <Space>
            <Tooltip title="Move up">
              <Button 
                size="small" 
                icon={<ArrowUpOutlined />} 
                onClick={() => handleMoveTransitionSequence(record.name, 'up')}
                disabled={!canMoveUp}
              />
            </Tooltip>
            <Tooltip title="Move down">
              <Button 
                size="small" 
                icon={<ArrowDownOutlined />} 
                onClick={() => handleMoveTransitionSequence(record.name, 'down')}
                disabled={!canMoveDown}
              />
            </Tooltip>
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEditTransition(record)} />
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: 'Delete Transition',
                  icon: <ExclamationCircleOutlined />,
                  content: `Are you sure you want to delete "${record.name}"?`,
                  onOk: () => handleDeleteTransition(record.name)
                });
              }}
            />
          </Space>
        );
      }
    }
  ];

  // Property columns
  const propertyColumns = [
    {
      title: 'Property Key',
      dataIndex: 'property_key',
      key: 'property_key',
      render: (text: string, _record: StateTemplatePropertyMapping) => (
        <Button type="link" onClick={() => handleEditProperty(text)} style={{ padding: 0 }}>
          <strong>{text}</strong>
        </Button>
      )
    },
    {
      title: 'Type',
      dataIndex: 'property_type',
      key: 'property_type',
      render: (type: string) => <Tag color="blue">{type}</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: StateTemplatePropertyMapping) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEditProperty(record.property_key)} />
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: 'Remove Property',
                icon: <ExclamationCircleOutlined />,
                content: `Are you sure you want to remove "${record.property_key}" from this template?`,
                onOk: () => handleDeleteProperty(record.id, record.property_key)
              });
            }}
          />
        </Space>
      )
    }
  ];

  // Details tab content
  const detailsTabContent = (
    <>
      <Form.Item
        label="Template Name"
        name="name"
        rules={[{ required: true, message: 'Please enter template name' }]}
      >
        <Input placeholder="e.g., Operational Lifecycle" />
      </Form.Item>

      <Form.Item
        label="Version"
        name="version"
        rules={[{ required: true, message: 'Please enter version' }]}
      >
        <Input placeholder="e.g., 1.0.0" />
      </Form.Item>

      <Form.Item
        label="Description"
        name="description"
      >
        <TextArea rows={3} placeholder="Describe this template" />
      </Form.Item>

      <Form.Item
        label="Tags"
        name="tags"
        getValueFromEvent={(value) => value || []}
        normalize={(value) => Array.isArray(value) ? value : []}
      >
        <Select
          mode="tags"
          placeholder="Add tags (optional)"
          tokenSeparators={[',']}
        />
      </Form.Item>

      {copyingFromTemplate && (
        <div style={{ marginBottom: 16, padding: 12, background: '#e6f7ff', borderRadius: 4 }}>
          <p style={{ margin: 0, color: '#1890ff' }}>
            <strong>Copying from:</strong> {copyingFromTemplate.name} (v{copyingFromTemplate.version})
          </p>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
            This will copy {copyingFromTemplate.states?.length || 0} states, {copyingFromTemplate.transitions?.length || 0} transitions, 
            and {copyingFromTemplate.property_mappings?.length || 0} properties
          </p>
        </div>
      )}
    </>
  );

  // Render right panel content
  const renderRightPanel = () => {
    if (isCreatingTemplate) {
      return (
        <ManagementForm
          title="Create Template"
          isCreating={true}
          isLoading={false}
          isSaving={isSavingTemplate}
          form={templateForm}
          onSave={handleSaveTemplate}
          onCancel={handleCancelTemplate}
          showDelete={false}
        >
          {detailsTabContent}
        </ManagementForm>
      );
    }

    if (!selectedTemplate) {
      return (
        <Card
          title={
            <Title level={4} style={{ margin: 0 }}>
              Select a Template
            </Title>
          }
          className={styles.propertyDetailsCard}
        >
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary">
              Select a template from the list to view and edit its details.
            </Text>
          </div>
        </Card>
      );
    }

    return (
      <Card
        title={
          <Title level={4} style={{ margin: 0 }}>
            Template: {selectedTemplate.name}
          </Title>
        }
        className="property-details-card"
        extra={
          <Space>
            <Button
              icon={<CopyOutlined />}
              onClick={handleCopyTemplate}
              size="small"
            >
              Copy
            </Button>
          </Space>
        }
      >
        <Tabs
          activeKey={activeDetailsTab}
          onChange={setActiveDetailsTab}
          items={[
            {
              key: 'details',
              label: 'Details',
              children: (
                <ManagementForm
                  title={selectedTemplate.name}
                  isCreating={false}
                  isLoading={false}
                  isSaving={isSavingTemplate}
                  form={templateForm}
                  onSave={handleSaveTemplate}
                  onCancel={handleCancelTemplate}
                  onDelete={handleDeleteTemplate}
                  showDelete={true}
                >
                  {detailsTabContent}
                </ManagementForm>
              )
            },
            {
              key: 'properties',
              label: `Properties (${propertyMappings.length})`,
              children: (
                <div>
                  <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddProperty}>
                      Add Property
                    </Button>
                    <Button icon={<PlusOutlined />} onClick={handleCreateNewProperty}>
                      Create New Property
                    </Button>
                  </div>
                  {propertyMappings.length > 0 ? (
                    <Table
                      dataSource={propertyMappings}
                      columns={propertyColumns}
                      rowKey={(record) => record.id || record.property_key || `prop-${record.property_key}`}
                      pagination={false}
                      size="small"
                    />
                  ) : (
                    <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                      No properties in this template. Click "Add Property" to add them.
                    </p>
                  )}
                </div>
              )
            },
            {
              key: 'states',
              label: `States (${filteredStates.length}/${states.length})`,
              children: (
                <>
                  <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddState}>
                      Add State
                    </Button>
                    <Select
                      value={stateFilter}
                      onChange={setStateFilter}
                      style={{ width: 150 }}
                    >
                      <Option value="all">All States</Option>
                      <Option value="improvement">Improvement</Option>
                      <Option value="operational">Operational</Option>
                    </Select>
                  </div>
                  <Table
                    dataSource={sortedStates}
                    columns={stateColumns}
                    rowKey={(record) => record.id || record.name}
                    pagination={false}
                    size="small"
                  />
                </>
              )
            },
            {
              key: 'transitions',
              label: `Transitions (${filteredTransitions.length}/${transitions.length})`,
              children: (
                <>
                  <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTransition}>
                      Add Transition
                    </Button>
                    <Select
                      value={transitionFilter}
                      onChange={setTransitionFilter}
                      style={{ width: 150 }}
                    >
                      <Option value="all">All Transitions</Option>
                      <Option value="improvement">Improvement</Option>
                      <Option value="operational">Operational</Option>
                    </Select>
                  </div>
                  <Table
                    dataSource={sortedTransitions}
                    columns={transitionColumns}
                    rowKey={(record) => record.id || record.name}
                    pagination={false}
                    size="small"
                  />
                </>
              )
            }
          ]}
        />
      </Card>
    );
  };

  return (
    <>
      <Row gutter={16} style={{ height: '100%' }}>
        {/* Left Pane - Templates List */}
        <Col xs={24} lg={12} xl={12}>
          <Card
            title={
              <Space>
                <Title level={4} style={{ margin: 0 }}>Templates</Title>
                <Tag>{filteredTemplates.length} templates</Tag>
              </Space>
            }
            extra={
              <Space>
                <Search
                  placeholder="Search templates..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 200 }}
                  allowClear
                />
                <Button icon={<ReloadOutlined />} onClick={fetchTemplates} size="small">Refresh</Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreateTemplate}
                  size="small"
                >
                  Create
                </Button>
              </Space>
            }
            className={styles.propertiesListCard}
          >
            <Table
              columns={columns}
              dataSource={filteredTemplates}
              loading={loading}
              size="small"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} templates`,
                position: ['bottomCenter']
              }}
              rowKey="id"
              scroll={{ y: 'calc(100vh - 400px)' }}
              onRow={(record) => ({
                onClick: () => handleRowClick(record),
                style: {
                  cursor: 'pointer',
                  backgroundColor: selectedTemplate?.id === record.id ? '#e6f7ff' : 'transparent'
                }
              })}
              rowClassName={(record) =>
                selectedTemplate?.id === record.id ? styles.selectedPropertyRow : ''
              }
            />
          </Card>
        </Col>

        {/* Right Pane - Template Details */}
        <Col xs={24} lg={12} xl={12}>
          {renderRightPanel()}
        </Col>
      </Row>

      {/* State Modal */}
      <Modal
        title={editingState ? 'Edit State' : 'Add State'}
        open={showStateModal}
        onCancel={() => {
          setShowStateModal(false);
          stateForm.resetFields();
        }}
        onOk={handleSaveState}
        width={600}
      >
        <Form form={stateForm} layout="vertical">
          <Form.Item
            label="State Name"
            name="name"
            rules={[{ required: true, message: 'Please select state name' }]}
          >
            <Select placeholder="Select a standard state">
              <Select.OptGroup label="Operational States">
                {Object.values(StandardOperationalState).map(state => (
                  <Option key={state} value={state}>{state}</Option>
                ))}
              </Select.OptGroup>
              <Select.OptGroup label="Improvement States">
                {Object.values(StandardImprovementState).map(state => (
                  <Option key={state} value={state}>{state}</Option>
                ))}
              </Select.OptGroup>
            </Select>
          </Form.Item>

          <Form.Item label="Description" name="description">
            <TextArea rows={2} placeholder="Describe this state" />
          </Form.Item>

          <Form.Item
            label="State Type"
            name="state_type"
            initialValue="OPERATIONAL"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="OPERATIONAL">Operational</Option>
              <Option value="MATERIAL">Material</Option>
              <Option value="RESOURCE">Resource</Option>
              <Option value="CREATION">Creation</Option>
              <Option value="IMPROVEMENT">Improvement</Option>
            </Select>
          </Form.Item>

          <Form.Item name="is_initial" valuePropName="checked" initialValue={false}>
            <Checkbox>Initial State</Checkbox>
          </Form.Item>

          <Form.Item name="is_final" valuePropName="checked" initialValue={false}>
            <Checkbox>Final State</Checkbox>
          </Form.Item>

          <Form.Item name="is_improvement" valuePropName="checked" initialValue={false}>
            <Checkbox>Improvement State</Checkbox>
          </Form.Item>

          <Form.Item name="is_resource_active" valuePropName="checked" initialValue={false}>
            <Checkbox>Resource Active (Available as Resource)</Checkbox>
          </Form.Item>

          <Form.Item label="Sequence Order" name="sequence_order" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Transition Modal */}
      <Modal
        title={editingTransition ? 'Edit Transition' : 'Add Transition'}
        open={showTransitionModal}
        onCancel={() => {
          setShowTransitionModal(false);
          transitionForm.resetFields();
          setTransitionPropertyChanges([]);
          setTransitionValidationRules({});
        }}
        onOk={handleSaveTransition}
        width={1000}
      >
        <Tabs
          activeKey={transitionModalActiveTab}
          onChange={setTransitionModalActiveTab}
          items={[
            {
              key: 'basic',
              label: 'Basic Info',
              children: (
                <Form form={transitionForm} layout="vertical">
                  <Form.Item
                    label="Transition Name"
                    name="name"
                    rules={[{ required: true, message: 'Please select transition name' }]}
                  >
                    <Select placeholder="Select a standard transition">
                      {Object.values(StandardTransition).map(transition => (
                        <Option key={transition} value={transition}>{transition}</Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item label="Description" name="description">
                    <TextArea rows={2} placeholder="Describe this transition" />
                  </Form.Item>

                  <Form.Item 
                    label="From State" 
                    name="from_state"
                    tooltip="Leave empty for CREATION transitions"
                  >
                    <Select 
                      placeholder="Leave empty for CREATION" 
                      allowClear
                      onChange={(value) => {
                        const toState = transitionForm.getFieldValue('to_state');
                        if (value && toState) {
                          const isImprovement = autoSetTransitionImprovement(value, toState);
                          transitionForm.setFieldsValue({ is_improvement: isImprovement });
                        }
                      }}
                    >
                      {states.map(state => (
                        <Option key={state.name} value={state.name}>
                          {state.name} {state.is_improvement && <Tag color="purple" style={{ marginLeft: 4 }}>I</Tag>}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="To State"
                    name="to_state"
                    rules={[{ required: true, message: 'Please select target state' }]}
                  >
                    <Select 
                      placeholder="Select target state"
                      onChange={(value) => {
                        const fromState = transitionForm.getFieldValue('from_state');
                        if (value) {
                          const isImprovement = autoSetTransitionImprovement(fromState, value);
                          transitionForm.setFieldsValue({ is_improvement: isImprovement });
                        }
                      }}
                    >
                      {states.map(state => (
                        <Option key={state.name} value={state.name}>
                          {state.name} {state.is_improvement && <Tag color="purple" style={{ marginLeft: 4 }}>I</Tag>}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Transition Type"
                    name="default_transition_type"
                    initialValue="PROCESSITEM"
                    rules={[{ required: true }]}
                  >
                    <Select>
                      <Option value="PROCESSITEM">Process Item</Option>
                      <Option value="MATERIAL">Material</Option>
                      <Option value="RESOURCE">Resource</Option>
                      <Option value="CREATION">Creation</Option>
                      <Option value="PROCESSITEM_PERSISTENT">Process Item (Persistent)</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item name="is_secondary" valuePropName="checked" initialValue={false}>
                    <Checkbox>Secondary Transition (Optional/Backtracking)</Checkbox>
                  </Form.Item>

                  <Form.Item 
                    name="is_improvement" 
                    valuePropName="checked" 
                    initialValue={false}
                    tooltip="Auto-set based on states"
                  >
                    <Checkbox>Improvement Transition</Checkbox>
                  </Form.Item>

                  <Form.Item label="Sequence Order" name="sequence_order" initialValue={0}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Form>
              )
            },
            {
              key: 'property-operations',
              label: 'Property Operations',
              children: (
                <PropertyChangesEditor
                  propertyChanges={transitionPropertyChanges}
                  onChange={setTransitionPropertyChanges}
                  artifactProperties={propertyMappings.map(p => ({
                    property_key: p.property_key,
                    name: p.property_key,
                    property_type: p.property_type,
                    type: p.property_type,
                    description: undefined
                  }))}
                />
              )
            },
            {
              key: 'validation-rules',
              label: 'Validation Rules',
              children: (
                <ValidationRulesEditor
                  validationRules={transitionValidationRules}
                  onChange={setTransitionValidationRules}
                  artifactProperties={propertyMappings.map(p => ({
                    property_key: p.property_key,
                    name: p.property_key,
                    property_type: p.property_type,
                    type: p.property_type,
                    description: undefined
                  }))}
                  transitions={transitions.map(t => ({
                    name: t.name,
                    id: t.id || '',
                    description: t.description
                  }))}
                />
              )
            }
          ]}
        />
      </Modal>

      {/* Property Selector Modal */}
      {selectedTemplate && (
        <GlobalPropertySelector
          visible={showPropertySelector}
          templateId={selectedTemplate.id}
          onCancel={() => setShowPropertySelector(false)}
          onSelect={handlePropertySelect}
          excludeProperties={propertyMappings.map(p => p.property_key)}
        />
      )}
    </>
  );
};
